import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CUSTOM_BUDGET_TIER_CODE,
  ThematicPackageBudgetTierId,
  ThematicPackageBudgetTierIds,
} from '@features/campaigns/shared/types/campaignSubmit.ts';
import { UpsellOptionCard } from '@features/campaigns/thematicPackage/components/UpsellOptionCard/UpsellOptionCard.tsx';
import {
  ThematicPackageUpsellFields,
  ThematicPackageUpsellFormValues,
} from '@features/campaigns/thematicPackage/containers/ThematicPackageUpsellPage/ThematicPackageUpsellPage.types.ts';
import { BudgetTier } from '@features/campaigns/thematicPackage/types/thematicPackage.types.ts';
import { Icon } from '@shared/components/Icon/Icon.tsx';
import { ICON_VARIANTS, IconVariants } from '@shared/components/Icon/IconMapper.ts';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { formatNumberAsMoney } from '@shared/utils/moneyFormat.ts';

import styles from './FieldBudgetTier.module.scss';

const BUDGET_TIER_GROUP_NAME = 'thematic-package-budget-tier';

// Part of the e2e contract, so it stays lowercase even though the API tier code is uppercase.
const CUSTOM_BUDGET_TEST_ID_SUFFIX = 'custom';

const ICON_VARIANT_BY_TIER_ID: Record<ThematicPackageBudgetTierId, IconVariants> = {
  [ThematicPackageBudgetTierIds.TIER_1]: ICON_VARIANTS.wallet,
  [ThematicPackageBudgetTierIds.TIER_2]: ICON_VARIANTS.walletChart,
  [ThematicPackageBudgetTierIds.TIER_3]: ICON_VARIANTS.walletPlus,
};

interface FieldBudgetTierProps {
  budgetTiers: BudgetTier[];
}

export function FieldBudgetTier({ budgetTiers }: FieldBudgetTierProps) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const { control, setValue } = useFormContext<ThematicPackageUpsellFormValues>();

  return (
    <Controller
      control={control}
      name={ThematicPackageUpsellFields.BUDGET_TIER_ID}
      render={({ field }) => {
        const selectBudgetTier = (value: string) => {
          field.onChange(value);

          if (value !== CUSTOM_BUDGET_TIER_CODE) {
            setValue(ThematicPackageUpsellFields.CUSTOM_BUDGET_AMOUNT, null);
          }
        };

        return (
          <div className={styles.grid}>
            {budgetTiers.map(({ id, amount, isSelectable }) => {
              const isCustomBudget = id === null;
              const value = id ?? CUSTOM_BUDGET_TIER_CODE;
              const dataTestId = `budget-tier-${id ?? CUSTOM_BUDGET_TEST_ID_SUFFIX}`;
              const amountLabel = amount === null ? undefined : formatNumberAsMoney(amount);

              return (
                <UpsellOptionCard
                  key={dataTestId}
                  inputId={dataTestId}
                  groupName={BUDGET_TIER_GROUP_NAME}
                  value={value}
                  label={isCustomBudget ? t('thematicPackage.budget.customLabel') : undefined}
                  amountLabel={amountLabel}
                  helperText={isCustomBudget ? t('thematicPackage.budget.customHelperText') : undefined}
                  icon={
                    <Icon
                      variant={isCustomBudget ? ICON_VARIANTS.walletEdit : ICON_VARIANT_BY_TIER_ID[id]}
                      iconAltText={
                        isCustomBudget
                          ? t('thematicPackage.budget.customLabel')
                          : t('thematicPackage.budget.tierIconAlt', { amount: amountLabel })
                      }
                    />
                  }
                  isSelected={field.value === value}
                  isDisabled={!isSelectable}
                  selectCallback={selectBudgetTier}
                  dataTestId={dataTestId}
                />
              );
            })}
          </div>
        );
      }}
    />
  );
}
